import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { calendarConnect, calendarDisconnect, calendarStatus, calendarSync } from '../../../api/calendar';

import './google-calendar-card.style.scss';

const DEFAULT_STATUS = {
  enabled: true,
  available: false,
  connected: false,
  email: null,
  eventsCount: 0,
  seatsFull: false,
  scopeMissing: false,
};

// Українські форми для «заняття» — щоб не писати «1 занять».
const lessonsLabel = (count) => {
  const tail = count % 100;
  const last = count % 10;

  if (tail > 10 && tail < 20) {
    return 'занять';
  }

  return last >= 1 && last <= 4 ? 'заняття' : 'занять';
};

const DEFAULT_ERROR = 'Не вдалося підключити Google Calendar. Спробуй ще раз.';

// Публічний сайт школи. Свідомо константа, а не REACT_APP_*: нова env-змінна —
// це ще один крок деплою, який легко забути, і тоді посилання на пояснення
// інтеграції та політику конфіденційності мовчки поїдуть у нікуди.
const SITE_URL = 'https://newbrain.com.ua';
const INTEGRATION_URL = `${SITE_URL}/google-calendar`;
const PRIVACY_URL = `${SITE_URL}/privacy-policy`;

// Бекенд віддає в адресу лише КОД (див. Service::ERROR_CODES) — текст складаємо тут,
// щоб довільний рядок з query не можна було видати за наше повідомлення.
const ERROR_TEXTS = {
  access_denied: 'Ти не підтвердив доступ у вікні Google. Спробуй ще раз і натисни «Дозволити».',
  invalid_grant: 'Google відхилив підтвердження: посилання вже використане або застаріле. Спробуй ще раз.',
  redirect_uri_mismatch: 'Помилка налаштування на нашому боці. Ми вже про неї знаємо — напиши менеджеру.',
  incomplete_response: 'Google повернув неповну відповідь. Спробуй підключити ще раз.',
  state_expired: 'Посилання на підключення застаріло. Натисни «Підключити» ще раз.',
  google_unavailable: 'Google не відповів. Спробуй ще раз за хвилину.',
  no_access_token: 'Google не передав токен доступу. Спробуй ще раз.',
  not_configured: 'Підключення тимчасово недоступне. Ми вже про це знаємо.',
  missing_scope: 'У вікні Google не була відмічена галочка доступу до календаря, тому підключення не відбулось. Натисни «Підключити» ще раз і постав галочку.',
  unknown: DEFAULT_ERROR,
};

const readErrorText = (error, fallback) => {
  const message = error?.response?.data?.info?.message;

  if (message) {
    return message;
  }

  const code = error?.response?.status;

  return code ? `${fallback} (код ${code})` : fallback;
};

const GoogleLogo = () => (
  <svg className="gcal__g" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2.5 24 .5 14.6.5 6.5 5.9 2.6 13.7l7.8 6.1C12.3 13.9 17.6 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.2 7.1-17.6z" />
    <path fill="#FBBC05" d="M10.4 28.2c-.5-1.4-.8-2.9-.8-4.2s.3-2.8.8-4.2l-7.8-6.1C.9 16.7 0 20.2 0 24s.9 7.3 2.6 10.3l7.8-6.1z" />
    <path fill="#34A853" d="M24 47.5c6.2 0 11.5-2 15.4-5.6l-7.6-5.9c-2.1 1.4-4.8 2.3-7.8 2.3-6.4 0-11.7-4.4-13.6-10.1l-7.8 6.1C6.5 42.1 14.6 47.5 24 47.5z" />
  </svg>
);

const CalendarIcon = ({ connected, failed }) => {
  let tone = '#6b298a';

  if (connected) {
    tone = '#7d9200';
  } else if (failed) {
    tone = '#E9271A';
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" width="26" height="26">
      <rect x="3" y="5" width="18" height="16" rx="3" stroke={tone} strokeWidth="1.7" />
      <path d="M3 10h18" stroke={tone} strokeWidth="1.7" />
      <path d="M8 3v4M16 3v4" stroke={tone} strokeWidth="1.7" strokeLinecap="round" />
      {connected
        ? <path d="M8.5 15.3l2.2 2.2 4.6-4.6" stroke={tone} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        : <rect x="7" y="13" width="4" height="4" rx="1" fill={tone} />}
    </svg>
  );
};

CalendarIcon.propTypes = {
  connected: PropTypes.bool,
  failed: PropTypes.bool,
};

const CheckIcon = () => (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="10" fill="#f1f6dd" />
    <path d="M6 10.2l2.6 2.6L14 7.4" stroke="#7d9200" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M10 6v5M10 13.6v.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const DenyIcon = () => (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="10" fill="#f0f1f3" />
    <path d="M7 7l6 6M13 7l-6 6" stroke="#6D7280" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

const OkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M6 10.2l2.6 2.6L14 7.4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Картка підключення Google Calendar у налаштуваннях кабінету.
 *
 * Стан навмисно локальний (без Redux): дані потрібні лише цій картці, а спільні
 * store-барелі (`store/reducer|actions|types/index.js`) паралельно переписує
 * пакет «Тренування» — зайвий домен там створив би конфлікт реєстрів.
 */
const GoogleCalendarCard = ({ token, flash, onFlashClose }) => {
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [loading, setLoading] = useState(true);
  const [statusFailed, setStatusFailed] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);

  // Миттєвий перенос запускаємо рівно один раз на повернення з Google.
  const instantSyncStarted = useRef(false);

  const justConnected = flash?.status === 'success';

  const loadStatus = useCallback(async () => {
    if (!token) {
      setLoading(false);

      return;
    }

    try {
      const response = await calendarStatus({ token });
      const data = response?.data?.data?.calendar;

      if (!data) {
        throw new Error('empty status payload');
      }

      setStatus({
        enabled: Boolean(data.enabled),
        available: Boolean(data.available),
        connected: Boolean(data.connected),
        email: data.email || null,
        eventsCount: Number(data.events_count) || 0,
        // Квота Google: поки застосунок на верифікації, згоду можуть дати лише
        // обмежена кількість акаунтів. Понад ліміт Google показав би
        // «access_blocked» на своєму екрані — тому кнопку ховаємо ще до переходу.
        seatsFull: Boolean(data.seats_full),
        scopeMissing: Boolean(data.scope_missing),
      });
      setStatusFailed(false);
    } catch (requestError) {
      setStatusFailed(true);
      setError(readErrorText(requestError, 'Не вдалося перевірити стан підключення.'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (flash?.status === 'error') {
      setError(ERROR_TEXTS[flash.code] || DEFAULT_ERROR);
    }
  }, [flash]);

  // Після підключення переносимо найближчий тиждень одразу: інакше людина
  // повернулась би з Google у порожній календар і не зрозуміла, чи спрацювало
  // (решту до 21 дня добере фонова синхронізація).
  useEffect(() => {
    if (!justConnected || !status.connected || !token || instantSyncStarted.current) {
      return;
    }

    instantSyncStarted.current = true;

    const runInstantSync = async () => {
      setSyncing(true);
      setSyncFailed(false);

      try {
        const response = await calendarSync({ token });
        const data = response?.data?.data?.calendar;

        if (data) {
          setStatus((previous) => ({
            ...previous,
            eventsCount: Number(data.events_count) || 0,
          }));

          // Частину не встигли перенести за відведений час — кажемо чесно,
          // інакше людина побачила б «Готово!» і порожній календар.
          if (Number(data.pending) > 0) {
            setSyncFailed(true);
          }
        }
      } catch (requestError) {
        if (requestError?.response?.status === 409) {
          // Підключення насправді неробоче — тут «доїде саме» неправда.
          setError(readErrorText(requestError, 'Календар потребує перепідключення.'));
        } else {
          // Календар підключений, просто заняття доїдуть трохи пізніше самі.
          setSyncFailed(true);
        }
      } finally {
        setSyncing(false);
      }
    };

    runInstantSync();
  }, [justConnected, status.connected, token]);

  const retryStatus = () => {
    setLoading(true);
    setError('');
    loadStatus();
  };

  const submitConnect = async () => {
    setBusy('connect');
    setError('');

    try {
      const response = await calendarConnect({ token });
      const authUrl = response?.data?.data?.auth_url;

      if (!authUrl) {
        throw new Error('empty auth_url');
      }

      if (onFlashClose) {
        onFlashClose();
      }

      window.location.href = authUrl;
    } catch (requestError) {
      setBusy('');
      setError(readErrorText(requestError, 'Не вдалося почати підключення.'));
    }
  };

  const submitDisconnect = async () => {
    setBusy('disconnect');
    setError('');

    try {
      await calendarDisconnect({ token });

      setStatus((previous) => ({ ...previous, connected: false, email: null, eventsCount: 0 }));
      setConfirming(false);
      instantSyncStarted.current = false;

      // Власне відключення звільнило місце — перечитуємо стан, інакше кнопка
      // «Підключити» лишилась би схованою до перезавантаження сторінки.
      loadStatus();

      if (onFlashClose) {
        onFlashClose();
      }
    } catch (requestError) {
      setError(readErrorText(requestError, 'Не вдалося відключити календар.'));
    } finally {
      setBusy('');
    }
  };

  // Блок ховаємо повністю: поки не знаємо стану (щоб не блимав), якщо інтеграція
  // не налаштована на сервері, або якщо синхронізація цьому студенту не дозволена.
  if (loading || (!statusFailed && (!status.enabled || !status.available))) {
    return null;
  }

  const errorNote = (
    <div className="gcal__note gcal__note--err" role="alert">
      <AlertIcon />
      <span>{error}</span>
    </div>
  );

  return (
    <div className="settings-block gcal-block">
      <h3 className="settings-block__title">Синхронізація з календарем</h3>

      <div className="gcal">
        {/* Статус не прочитався: не вдаємо «не підключено» і не тягнемо людину
            на повторний OAuth — чесно кажемо, що не змогли перевірити. */}
        {!loading && statusFailed && (
          <>
            <div className="gcal__top">
              <div className="gcal__icon gcal__icon--err">
                <CalendarIcon failed />
              </div>
              <div className="gcal__headings">
                <h4 className="gcal__name">Google Calendar</h4>
                <p className="gcal__desc">Не вдалося перевірити стан підключення.</p>
              </div>
            </div>

            {error && errorNote}

            <div className="gcal__actions">
              <button type="button" className="gcal__btn gcal__btn--ghost" onClick={retryStatus}>
                Оновити
              </button>
            </div>
          </>
        )}

        {!loading && !statusFailed && (
          <>
            {justConnected && status.connected && !syncing && (
              <div className="gcal__note gcal__note--ok gcal__note--lead">
                <OkIcon />
                <span><b>Готово!</b> Google Calendar підключено до твого акаунта.</span>
              </div>
            )}

            {syncing && (
              <div className="gcal__note gcal__note--info gcal__note--lead" role="status">
                <span className="gcal__spinner" aria-hidden="true" />
                <span>Переносимо твій розклад у календар…</span>
              </div>
            )}

            {syncFailed && !syncing && (
              <div className="gcal__note gcal__note--warn gcal__note--lead">
                <AlertIcon />
                <span>Заняття зʼявляться в календарі протягом кількох хвилин.</span>
              </div>
            )}

            <div className="gcal__top">
              <div className={`gcal__icon${status.connected ? ' gcal__icon--on' : ''}${error && !status.connected ? ' gcal__icon--err' : ''}`}>
                <CalendarIcon connected={status.connected} failed={Boolean(error)} />
              </div>

              <div className="gcal__headings">
                <h4 className="gcal__name">
                  Google Calendar
                  {status.connected
                    ? <span className="gcal__chip gcal__chip--on"><span className="gcal__dot" />Підключено</span>
                    : <span className={`gcal__chip ${error ? 'gcal__chip--err' : 'gcal__chip--off'}`}><span className="gcal__dot" />Не підключено</span>}
                </h4>

                <p className="gcal__desc">
                  {status.connected
                    ? 'Заняття, пробні та відпрацювання зʼявляються у твоєму календарі автоматично.'
                    : 'Підключи свій Google-акаунт — і заняття з розкладу зʼявлятимуться в календарі телефона автоматично.'}
                </p>
              </div>
            </div>

            {!status.connected && !error && !status.seatsFull && (
              <ul className="gcal__perks">
                <li><CheckIcon />Нагадування від Google перед кожним заняттям</li>
                <li><CheckIcon />Пробні та відпрацювання теж потрапляють у календар</li>
                <li><CheckIcon />Перенесли або скасували заняття — календар оновиться сам</li>
              </ul>
            )}

            {status.connected && status.email && (
              <div className="gcal__account">
                <div className="gcal__avatar">{status.email.charAt(0)}</div>
                <div className="gcal__account-txt">
                  <div className="gcal__account-label">Google-акаунт</div>
                  <div className="gcal__account-mail">{status.email}</div>
                </div>

                {status.eventsCount > 0 && (
                  <div className="gcal__count">
                    <b>{status.eventsCount}</b>
                    <span>{lessonsLabel(status.eventsCount)} у календарі</span>
                  </div>
                )}
              </div>
            )}

            {status.connected && status.scopeMissing && (
              <div className="gcal__note gcal__note--warn">
                <AlertIcon />
                <span>
                  <b>Немає дозволу на календар.</b> Акаунт підключено, але у вікні Google не була
                  відмічена галочка доступу до календаря — тому події не створюються і розклад не
                  переноситься. Натисни «Змінити акаунт» і постав галочку.
                </span>
              </div>
            )}

            {error && errorNote}

            {confirming && status.connected && (
              <div className="gcal__note gcal__note--warn">
                <AlertIcon />
                <span>
                  Відключити календар? Ми видалимо з нього <b>всі</b> події, які створив New Brain —
                  і майбутні, і минулі заняття, і ті, що ти додав сам у кабінеті. Твої власні події
                  Google лишаться недоторканими. Якщо подій багато, прибирання може зайняти кілька хвилин.
                </span>
              </div>
            )}

            {/* Розкриття ПЕРЕД згодою: людина має бачити обсяг доступу до того, як
                піде на екран Google. Це ж пояснення Google перевіряє при верифікації
                застосунку, тому текст навмисно дослівно збігається з розділом
                «Інтеграція з Google Календарем» у політиці конфіденційності. */}
            {!status.connected && !status.seatsFull && (
              <div className="gcal__disclosure">
                <div className="gcal__disclosure-title">Який доступ ми запитуємо</div>

                <ul className="gcal__disclosure-list">
                  <li>
                    <CheckIcon />
                    <span>Створюємо, оновлюємо та видаляємо у твоєму календарі <b>лише події занять New Brain</b></span>
                  </li>
                  <li>
                    <DenyIcon />
                    <span>Не читаємо й не змінюємо твої власні події</span>
                  </li>
                  <li>
                    <DenyIcon />
                    <span>Не маємо доступу до пошти, файлів, контактів і фото Google</span>
                  </li>
                </ul>

                <p className="gcal__disclosure-note">
                  Відключити можна будь-коли в цьому ж блоці: ми відкликаємо доступ у Google
                  і видаляємо з календаря всі події, які створив New Brain, — і майбутні, і минулі.
                </p>

                <p className="gcal__disclosure-links">
                  <a href={INTEGRATION_URL} target="_blank" rel="noopener noreferrer">Як працює інтеграція</a>
                  <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Політика конфіденційності</a>
                </p>
              </div>
            )}

            {/* Місць немає: кнопку не показуємо взагалі — Google усе одно
                зустрів би людину помилкою «access_blocked» на своєму екрані. */}
            {!status.connected && status.seatsFull && (
              <div className="gcal__note gcal__note--warn">
                <AlertIcon />
                <span>
                  <b>Зараз немає вільних місць.</b> Google дозволяє обмежену кількість підключень,
                  поки наш застосунок проходить перевірку. Спробуй трохи пізніше або напиши менеджеру.
                </span>
              </div>
            )}

            <div className="gcal__actions">
              {!status.connected && !status.seatsFull && (
                <button
                  type="button"
                  className="gcal__btn gcal__btn--google"
                  onClick={submitConnect}
                  disabled={busy === 'connect'}
                >
                  <GoogleLogo />
                  {busy === 'connect'
                    ? 'Відкриваємо Google…'
                    : (error ? 'Спробувати ще раз' : 'Підключити Google Calendar')}
                </button>
              )}

              {status.connected && !confirming && (
                <>
                  <button
                    type="button"
                    className="gcal__btn gcal__btn--ghost"
                    onClick={submitConnect}
                    disabled={busy === 'connect'}
                  >
                    {busy === 'connect' ? 'Відкриваємо Google…' : 'Змінити акаунт'}
                  </button>
                  <button
                    type="button"
                    className="gcal__btn gcal__btn--danger"
                    onClick={() => setConfirming(true)}
                  >
                    Відключити
                  </button>
                </>
              )}

              {status.connected && confirming && (
                <>
                  <button
                    type="button"
                    className="gcal__btn gcal__btn--danger"
                    onClick={submitDisconnect}
                    disabled={busy === 'disconnect'}
                  >
                    {busy === 'disconnect' ? 'Відключаємо…' : 'Так, відключити'}
                  </button>
                  <button
                    type="button"
                    className="gcal__btn gcal__btn--ghost"
                    onClick={() => setConfirming(false)}
                    disabled={busy === 'disconnect'}
                  >
                    Скасувати
                  </button>
                </>
              )}
            </div>

            {status.connected && (
              <div className="gcal__foot">
                Ми працюємо лише з подіями New Brain і не читаємо твої власні події. При відключенні
                видаляємо з календаря всі події, які створили.{' '}
                <a href={INTEGRATION_URL} target="_blank" rel="noopener noreferrer">Як працює інтеграція</a>
                {' · '}
                <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Політика конфіденційності</a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

GoogleCalendarCard.propTypes = {
  token: PropTypes.string,
  flash: PropTypes.shape({
    status: PropTypes.string,
    code: PropTypes.string,
  }),
  onFlashClose: PropTypes.func,
};

export default GoogleCalendarCard;
